import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import CustomModal from "components/modals/CustomModal";
import colors from "utils/colors";
import CustomText from "components/text/CustomText";
import CustomIcon from "components/icon/CustomIcon";
import { icons } from "utils/icons";
import Avatar, { AvatarSize } from "components/avatar/Avatar";
import { FaCheckCircle } from "react-icons/fa";
import { FixMeLater } from "types";
import { useLazyGetRoomListForForwardQuery, useLazyGetRoomsBySearchQuery, useUpdateRoomChannelApparanceMutation } from "app/services/room.service";
import { BUTTONS, EMPTY, PLACEHOLDERS, TITLES } from "locales/locales";
import CustomButton from "components/button/CustomButton";
import { useAppDispatch, useAppSelector } from "app/hooks";
import Spinner from "components/spinner/Spinner";
import { roomsActions } from "app/slices/rooms.slice";
import { groupsActions } from "app/slices/groups.slice";
import { filterUserNameFromRoomName } from "utils/hooks/useRoom";

interface IForwardProps {
  onClose: () => void;
  onForward: (roomIds: string[]) => void;
  isLoading: boolean;
}


const Forward = ({  onClose, onForward, isLoading }: IForwardProps) => {
  const { t } = useTranslation();
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [selectedRoomData, setSelectedRoomData] = useState<FixMeLater[]>([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRoomsLoading, setIsRoomsLoading] = useState(false);
  const [forwardRooms, setForwardRooms] = useState<FixMeLater[]>([]);
  const [getRoomListForForward] = useLazyGetRoomListForForwardQuery();
  const [getRoomsBySearch] = useLazyGetRoomsBySearchQuery();
  const myUser = useAppSelector(state => state.auth.user)
  const [updateRoomChannelApparance] = useUpdateRoomChannelApparanceMutation()
  const dispatch = useAppDispatch()


  useEffect(() => {
    async function _getRoomListForForward() {
      setIsRoomsLoading(true);
      try {
        const { data } = await getRoomListForForward()
        if (data?.data) setForwardRooms(data.data.filter((room: FixMeLater) => room?.type !== "MM"))
      } finally {
        setIsRoomsLoading(false);
      }
    }
    _getRoomListForForward()
  }, [])

  const appearRoom = useCallback(async () => {
    for (const forwardRoom of forwardRooms) {
      if (!forwardRoom.isAppear) {
        const result: FixMeLater = await updateRoomChannelApparance({
          roomId: forwardRoom._id,
          isAppear: true,
        })
        if (forwardRoom.type === 'DM' || forwardRoom.type === 'GM') {
          dispatch(roomsActions.makeRoomAppear(result?.data?.roomId))
        } else {
          dispatch(groupsActions.makeChannelAppear(result?.data?.roomId))
        }
      }
    }
  }, [forwardRooms, updateRoomChannelApparance, dispatch])

  useEffect(() => {
    const searchRooms = async () => {
      setIsRoomsLoading(true);
      try {
        if (searchText.trim()) {
          if (searchText.length > 2) {
            const {data} = await getRoomsBySearch(searchText)
            if (data?.data) {
              const searchResults = data.data
                .filter((room: FixMeLater) => room?.type !== "MM")
                .map((room: FixMeLater) => ({
                  ...room,
                  name: filterUserNameFromRoomName(room.name, room.type, myUser.fullname),
                }));
              setForwardRooms(searchResults);
            } else {
              setForwardRooms([]);
            }
          }
          setIsRoomsLoading(false);
        } else {
          const { data } = await getRoomListForForward()
          if (data?.data) {
            const allRooms = data.data
              .filter((room: FixMeLater) => room?.type !== "MM")
              .map((room: FixMeLater) => ({
                ...room,
                name: filterUserNameFromRoomName(room?.name, room.type, myUser.fullname),
              }));
            
            selectedRoomData.forEach(selectedRoom => {
              if (selectedRoom?.type === "MM") return;
              const exists = allRooms.some((room: FixMeLater) => room._id === selectedRoom._id);
              if (!exists) {
                allRooms.unshift(selectedRoom);
              }
            });
            setForwardRooms(allRooms);
          }
        }
      } finally {
        setIsRoomsLoading(false);
      }
    }
    searchRooms()
  }, [searchText, getRoomsBySearch, getRoomListForForward, myUser.fullname, selectedRoomData])


  const handleRoomSelect = useCallback((roomId: string) => {
    setSelectedRooms(prev => {
      if (prev.includes(roomId)) {
        setSelectedRoomData(curr => curr.filter(room => room._id !== roomId));
        return prev.filter(id => id !== roomId);
      } else {
        const roomData = forwardRooms.find(room => room._id === roomId);
        if (roomData) {
          setSelectedRoomData(curr => [roomData, ...curr]);
        }
        return [...prev, roomId];
      }
    });
  }, [forwardRooms]);

  const handleForward = useCallback(async () => {
    try {
      setLoading(true);
      await onForward(selectedRooms);
      await appearRoom()
      onClose();
    } finally {
      setLoading(false);
    }
  }, [onForward, selectedRooms, onClose]);

  //console.log(forwardRooms.filter(room => room.type === 'DM'));
  

  return (
    <CustomModal
      title={t(TITLES.FORWARD_TO_ROOMS_TITLE)}
      closeModal={onClose}
      showButtons={false}
      containerStyle={{ width: '500px', height: '600px' }}
    >
      <Container>
        <SearchInput
          placeholder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <RoomListContainer>
          {(isRoomsLoading || isLoading) && (
            <LoadingOverlay>
              <Spinner loading={true} />
            </LoadingOverlay>
          )}
          <RoomList>
            {forwardRooms?.length > 0 &&
              forwardRooms?.map((room: FixMeLater) => (
                <RoomItem
                  key={room._id}
                  onClick={() => handleRoomSelect(room._id)}
                  isSelected={selectedRooms.includes(room._id)}
                >
                  <RoomInfo>
                    <AvatarWrapper>
                      {room?.type === 'CM' ? (
                        <CustomIcon source={icons.channelActiveIcon} />
                      ) : room.type === 'DM' ? (
                        <Avatar
                          size={AvatarSize.small}
                          source={
                            room.users.filter((user: FixMeLater) => user._id !== myUser._id)[0]
                              ?.avatar || ''
                          }
                          text={room.name}
                        />
                      ) : (
                        <Avatar
                          size={AvatarSize.small}
                          source={room.avatar}
                          text={room?.members?.length?.toString()}
                        />
                      )}
                    </AvatarWrapper>
                    <TextContainer>
                      <CustomText typography="body1">{room?.name}</CustomText>
                    </TextContainer>
                  </RoomInfo>
                  {selectedRooms.includes(room._id) && (
                    <FaCheckCircle color={colors.primaryVime} size={25} />
                  )}
                </RoomItem>
              ))}
          </RoomList>
          {forwardRooms?.length === 0 && (
            <StyledEmptyContainer>
              <CustomText typography="body1">{t(EMPTY.EMPTY_NO_RESULTS)}</CustomText>
            </StyledEmptyContainer>
          )}
        </RoomListContainer>
        <Footer>
          <CustomButton
            onClick={handleForward}
            disabled={selectedRooms.length === 0 || loading}
            text={`${t(BUTTONS.FORWARD_TO_ROOMS)} (${selectedRooms.length})`}
          />
        </Footer>
      </Container>
    </CustomModal>
  )
};

export default Forward;

const StyledEmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  margin-top: 20px;
`;

const SearchInput = styled.input`
  padding: 12px;
  border: 1px solid ${colors.light80};
  border-radius: 8px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${colors.primaryVime};
  }
`;

const RoomItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${props => props.isSelected ? colors.light50 : 'transparent'};

  &:hover {
    background-color: ${colors.light50};
  }
`;

const RoomInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const AvatarWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${colors.light80};
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RoomListContainer = styled.div`
  flex: 1;
  overflow: hidden;
  min-height: 350px;
  max-height: 350px;
  overflow-y: auto;
  position: relative;
`;

const RoomList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  overflow-y: auto;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  z-index: 10;
`;
